<script lang="ts">
	import type { SvelteAsr, i18n } from 'types/common'
	import type { HomePurchaseOrders$result, HomeData$result } from '$houdini'
	import type { PurchaseOrderFilters } from './purchase-order'
	import HomeFilter from '../HomeFilter.svelte'
	import { toLocaleDateString } from '@isoftdata/utility-date-time'
	import { format as formatCurrency } from '@isoftdata/utility-currency'
	import Checkbox from '@isoftdata/svelte-checkbox'
	import Table, { type Column, Td } from '@isoftdata/svelte-table'
	import { booleanToYesNo } from '@isoftdata/utility-string'
	import { getContext } from 'svelte'
	import NoItemsForSelectedFilter from 'components/NoItemsForSelectedFilter.svelte'

	interface Props {
		asr: SvelteAsr
		purchaseOrders: HomePurchaseOrders$result['purchaseOrders']['items']
		stores: HomeData$result['stores']
		users: HomeData$result['userAccounts']
		userId: PurchaseOrderFilters['userId']
		storeId: PurchaseOrderFilters['storeId']
		showPricesFinalized: PurchaseOrderFilters['showPricesFinalized']
		showDoneReceiving: PurchaseOrderFilters['showDoneReceiving']
		dates: PurchaseOrderFilters['dates']
	}

	let {
		//
		asr,
		dates,
		purchaseOrders,
		stores,
		users,
		userId,
		storeId,
		showPricesFinalized,
		showDoneReceiving,
	}: Props = $props()

	const { t: translate } = getContext<i18n>('i18next')

	let displayPurchaseOrders = $derived(
		purchaseOrders.map(po => {
			return {
				...po,
				storeId: po.store?.id,
				source: po.vendor?.name || '',
				destination: po.store?.name || '',
				purchaser: po.salesPerson?.name || '',
			}
		}),
	)

	const columns: Array<Column<(typeof displayPurchaseOrders)[number]>> = [
		{ property: 'storeId', name: translate('common:storeNumber', 'Store #'), numeric: true },
		{
			property: 'purchaseOrderId',
			name: translate('home.purchaseOrder.purchaseOrderNumberColumn', 'PO #'),
			numeric: true,
		},
		{ property: 'date', name: translate('common:date', 'Date'), defaultSortColumn: true, defaultSortDirection: 'DESC' },
		{ property: 'source', name: translate('common:source', 'Source') },
		{ property: 'destination', name: translate('common:destination', 'Destination') },
		{ property: 'type', name: translate('common:type', 'Type') },
		{ property: 'purchaser', name: translate('common:purchaser', 'Purchaser') },
		{
			property: 'totalQuantityReceived',
			name: translate('common:qtyReceived', 'Qty Received'),
			numeric: true,
		},
		{
			property: 'receivedPercent',
			name: translate('home.purchaseOrder.percentReceivedColumn', '% Received'),
			numeric: true,
		},
		{ property: 'total', name: translate('common:total', 'Total'), numeric: true },
		{ property: 'doneReceiving', name: translate('common:doneReceiving', 'Done Receiving') },
		{ property: 'finalized', name: translate('common:finalized', 'Finalized') },
	]

	function filterChange() {
		asr.go(
			null,
			{
				userId: userId || null, //undefined will cause the parameter default to kick in, so pass null instead
				storeId,
				showDoneReceiving,
				showPricesFinalized,
				fromDate: dates?.from,
				toDate: dates?.to,
			},
			{ inherit: true },
		)
	}
</script>

<div class="row">
	<div class="col-md-3 col-lg-2">
		<HomeFilter
			bind:userId
			bind:storeId
			bind:dates
			{stores}
			{users}
			{filterChange}
			dateRangeSetting="defaultPurchaseOrderHistoryDateRange"
		>
			<Checkbox
				label={translate('showDoneReceiving', 'Show Done Receiving')}
				bind:checked={showDoneReceiving}
				onchange={filterChange}
			/>
			<Checkbox
				label={translate('showPricesFinalized', 'Show Prices Finalized')}
				bind:checked={showPricesFinalized}
				onchange={filterChange}
			/>
		</HomeFilter>
	</div>

	<div
		class="col-md-9 col-lg-10 tab-pane"
		id="sales"
		role="tab"
	>
		{#if displayPurchaseOrders.length > 0}
			<Table
				responsive
				{columns}
				perPageCount={15}
				rows={displayPurchaseOrders}
				hover={false}
			>
				{#snippet children({ row })}
					<tr class:text-muted={row.doneReceiving}>
						<Td property="storeId">{row.storeId}</Td>
						<Td property="purchaseOrderId">{row.purchaseOrderId}</Td>
						<Td property="date">{toLocaleDateString(row.date)}</Td>
						<Td property="source">{row.source}</Td>
						<Td property="destination">{row.destination}</Td>
						<Td property="type">{row.type}</Td>
						<Td property="purchaser">{row.purchaser}</Td>
						<Td property="totalQuantityReceived">{row.totalQuantityReceived}</Td>
						<Td property="receivedPercent">{row.receivedPercent}</Td>
						<Td property="total">{formatCurrency(row.total)}</Td>
						<Td property="doneReceiving">{booleanToYesNo(row.doneReceiving)}</Td>
						<Td property="finalized">{booleanToYesNo(row.finalized)}</Td>
					</tr>
				{/snippet}
			</Table>
		{:else}
			<NoItemsForSelectedFilter />
		{/if}
	</div>
</div>
